// The typed command layer: one registry of everything the app can *do*,
// each command a name + description + zod input schema + handler. The ⌘K
// palette surfaces these as action rows today; the same registry becomes the
// tool list for the AI chat integration (each command maps 1:1 to a tool
// call), so names are snake_case and descriptions are written for a model
// as much as for a human.

import { z } from 'zod';

/** Capabilities a command may need from the host UI. Headless callers (tests,
 * a future MCP server) can omit them — navigation simply becomes a no-op. */
export interface CommandContext {
  navigate: (to: string) => void;
}

export type CommandResult =
  | { ok: true; message: string; data?: unknown }
  | { ok: false; error: string };

export interface AppCommand<Schema extends z.ZodTypeAny = z.ZodTypeAny> {
  /** Stable snake_case identifier — becomes the tool name later. */
  name: string;
  /** Short human label (palette rows, chat confirmations). */
  title: string;
  /** When to use it and what it does — written for a model. */
  description: string;
  schema: Schema;
  run: (input: z.infer<Schema>, ctx: CommandContext) => CommandResult | Promise<CommandResult>;
}

/** Type-erased view for the registry: `run` takes `never` so any concretely
 * typed command is assignable (its input param is contravariant). */
export interface ErasedCommand {
  name: string;
  title: string;
  description: string;
  schema: z.ZodTypeAny;
  run: (input: never, ctx: CommandContext) => CommandResult | Promise<CommandResult>;
}

/** Identity helper so each command infers its own input type from its schema. */
export function defineCommand<Schema extends z.ZodTypeAny>(
  cmd: AppCommand<Schema>,
): AppCommand<Schema> {
  return cmd;
}

const registry = new Map<string, ErasedCommand>();

export function registerCommands(commands: ErasedCommand[]): void {
  // Duplicates *within one batch* are a programming error; re-registering an
  // existing name is last-wins so HMR module re-evaluation (which resets the
  // caller's module state but not this Map) just refreshes the handlers.
  const seen = new Set<string>();
  for (const cmd of commands) {
    if (seen.has(cmd.name)) throw new Error(`Duplicate command: ${cmd.name}`);
    seen.add(cmd.name);
    registry.set(cmd.name, cmd);
  }
}

export function getCommand(name: string): ErasedCommand | undefined {
  return registry.get(name);
}

export function listCommands(): ErasedCommand[] {
  return Array.from(registry.values());
}

/** Used by tests to isolate registrations. */
export function clearRegistry(): void {
  registry.clear();
}

const noopContext: CommandContext = { navigate: () => {} };

/**
 * Validate `input` against the command's schema and run it. Never throws —
 * unknown commands, schema failures and handler exceptions all come back as
 * `{ ok: false }` so a chat loop can hand the error straight to the model.
 */
export async function executeCommand(
  name: string,
  input: unknown = {},
  ctx?: Partial<CommandContext>,
): Promise<CommandResult> {
  const cmd = registry.get(name);
  if (!cmd) return { ok: false, error: `Unknown command: ${name}` };
  const parsed = cmd.schema.safeParse(input ?? {});
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `${i.path.join('.') || 'input'}: ${i.message}`)
      .join('; ');
    return { ok: false, error: `Invalid input for ${name} — ${issues}` };
  }
  try {
    return await cmd.run(parsed.data as never, { ...noopContext, ...ctx });
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : `Command ${name} failed.` };
  }
}
